import React, { useMemo, useState, useEffect } from 'react';
import { Card, Button } from '@dhis2/ui';
import { useDataEngine } from '@dhis2/app-runtime';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend
} from 'recharts';
import { CONFIG } from '../../leadership/api/LeadershipServices';

const RATING_SCALE_NAMESPACE = 'leadership-rating-scale';

// ============================================================
// HELPER FUNCTIONS - Defined OUTSIDE the component (FIRST)
// ============================================================

const getDefaultColor = (rating) => {
    const colors = {
        1: '#dc3545',
        2: '#ff9800',
        3: '#ffc107',
        4: '#4caf50',
        5: '#28a745'
    };
    return colors[rating] || '#6c757d';
};

const getDefaultScale = () => ({
    1: { label: '1 - Unsatisfactory', color: '#dc3545', description: 'Rarely meets expectations' },
    2: { label: '2 - Developing', color: '#ff9800', description: 'Sometimes meets expectations' },
    3: { label: '3 - Competent', color: '#ffc107', description: 'Meets expectations consistently' },
    4: { label: '4 - Strong Performer', color: '#4caf50', description: 'Frequently exceeds expectations' },
    5: { label: '5 - Role Model', color: '#28a745', description: 'Consistently exceeds expectations' }
});

const getDataStoreKey = (orgId) => orgId ? `scale_${orgId}` : 'scale_global';

const getParentOrgUnitId = async (engine, orgUnitId) => {
    if (!orgUnitId) return null;
    try {
        const response = await engine.query({
            orgUnit: {
                resource: `organisationUnits/${orgUnitId}`,
                params: { fields: 'path,parent[id]' }
            }
        });
        const path = response.orgUnit?.path;
        if (path) {
            const pathParts = path.split('/').filter(Boolean);
            if (pathParts.length >= 5) return pathParts[4];
            return response.orgUnit?.parent?.id || null;
        }
        return null;
    } catch (error) {
        return null;
    }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const BellCurveChart = ({
    title,
    data,
    period,
    onViewDetails,
    competencies = [],
    onCompetencyChange,
    showCompetencyFilter = true,
    ratingScale: propRatingScale = {},
    orgUnitId,
    appraisalData, 
    useAppraisalData = false 
}) => {
    const engine = useDataEngine();
    const [selectedCompetency, setSelectedCompetency] = useState('all');
    const [ratingScale, setRatingScale] = useState(propRatingScale);
    const [loadingScale, setLoadingScale] = useState(false);
    const [appraisalChartData, setAppraisalChartData] = useState([]);
    const [appraisalTotal, setAppraisalTotal] = useState(0);
    const [appraisalAverage, setAppraisalAverage] = useState(0);

    // ============================================================
    // HELPER FUNCTIONS - Defined INSIDE component AFTER state
    // ============================================================
    // These use the ratingScale state

    const getRatingLabel = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].label) {
            return ratingScale[rating].label;
        }
        return `${rating}`;
    };

    const getRatingColor = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].color) {
            return ratingScale[rating].color;
        }
        return getDefaultColor(rating);
    };

    const getRatingDescription = (rating) => {
        if (ratingScale && ratingScale[rating] && ratingScale[rating].description) {
            return ratingScale[rating].description;
        }
        return '';
    };

    // ============================================================
    // PROCESS APPRAISAL DATA
    // ============================================================

    const processAppraisalData = (appraisal) => {
        const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        
        // Extract task rating
        const taskRating = parseFloat(appraisal.employeeSelfRatingTask);
        if (!isNaN(taskRating) && taskRating >= 1 && taskRating <= 5) {
            distribution[Math.round(taskRating)] += 2;
        }

        // Extract behavioural rating
        const behaviouralRating = parseFloat(appraisal.employeeSelfRatingBehavioural);
        if (!isNaN(behaviouralRating) && behaviouralRating >= 1 && behaviouralRating <= 5) {
            distribution[Math.round(behaviouralRating)] += 2;
        }

        // Extract competency ratings
        try {
            const compRatings = appraisal.employeeCompetencyRatings 
                ? JSON.parse(appraisal.employeeCompetencyRatings) 
                : {};
            Object.values(compRatings).forEach(rating => {
                const numRating = parseFloat(rating);
                if (!isNaN(numRating) && numRating >= 1 && numRating <= 5) {
                    distribution[Math.round(numRating)] += 1;
                }
            });
        } catch (e) {
            console.warn('Failed to parse competency ratings:', e);
        }

        // Extract objective ratings if available
        if (appraisal.objectiveRatings) {
            appraisal.objectiveRatings.forEach(rating => {
                const numRating = parseFloat(rating);
                if (!isNaN(numRating) && numRating >= 1 && numRating <= 5) {
                    distribution[Math.round(numRating)] += 1;
                }
            });
        }

        return Object.entries(distribution).map(([rating, count]) => ({
            rating: parseInt(rating),
            count: count
        }));
    };

    // ============================================================
    // USE EFFECTS
    // ============================================================

    useEffect(() => {
        setRatingScale(propRatingScale);
    }, [propRatingScale]);

    useEffect(() => {
        if (useAppraisalData && appraisalData) {
            const processedData = processAppraisalData(appraisalData);
            setAppraisalChartData(processedData);
            const total = processedData.reduce((sum, item) => sum + item.count, 0);
            setAppraisalTotal(total);
            const avg = total > 0 
                ? processedData.reduce((sum, item) => sum + (item.rating * item.count), 0) / total 
                : 0;
            setAppraisalAverage(avg);
        }
    }, [useAppraisalData, appraisalData]);

    useEffect(() => {
        const fetchRatingScale = async () => {
            if (Object.keys(propRatingScale).length > 0) {
                setRatingScale(propRatingScale);
                setLoadingScale(false);
                return;
            }

            if (!orgUnitId) {
                setRatingScale(getDefaultScale());
                setLoadingScale(false);
                return;
            }

            setLoadingScale(true);
            try {
                const parentOrgId = await getParentOrgUnitId(engine, orgUnitId);
                const orgsToCheck = [orgUnitId];
                if (parentOrgId && parentOrgId !== orgUnitId) orgsToCheck.push(parentOrgId);

                let loadedScale = null;

                for (const orgId of orgsToCheck) {
                    const key = getDataStoreKey(orgId);
                    try {
                        const response = await engine.query({
                            ratingScale: {
                                resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`
                            }
                        });
                        const data = response.ratingScale;
                        if (Array.isArray(data) && data.length > 0) {
                            const mapped = {};
                            data.forEach((item) => {
                                const value = Number(item.value);
                                if (!Number.isNaN(value)) {
                                    mapped[value] = {
                                        label: item.label || `${value}`,
                                        color: item.color || getDefaultColor(value),
                                        description: item.description || ''
                                    };
                                }
                            });
                            if (Object.keys(mapped).length > 0) {
                                loadedScale = mapped;
                                break;
                            }
                        }
                    } catch (error) {
                        console.warn(`No rating scale found for org ${orgId}:`, error);
                    }
                }

                if (!loadedScale) {
                    try {
                        const globalResponse = await engine.query({
                            ratingScale: {
                                resource: `dataStore/${RATING_SCALE_NAMESPACE}/scale_global`
                            }
                        });
                        const globalData = globalResponse.ratingScale;
                        if (Array.isArray(globalData) && globalData.length > 0) {
                            const mapped = {};
                            globalData.forEach((item) => {
                                const value = Number(item.value);
                                if (!Number.isNaN(value)) {
                                    mapped[value] = {
                                        label: item.label || `${value}`,
                                        color: item.color || getDefaultColor(value),
                                        description: item.description || ''
                                    };
                                }
                            });
                            if (Object.keys(mapped).length > 0) {
                                loadedScale = mapped;
                            }
                        }
                    } catch (error) {
                        console.warn('No global rating scale found:', error);
                    }
                }

                setRatingScale(loadedScale || getDefaultScale());
            } catch (error) {
                console.error('Failed to fetch rating scale:', error);
                setRatingScale(getDefaultScale());
            } finally {
                setLoadingScale(false);
            }
        };

        fetchRatingScale();
    }, [orgUnitId, engine, propRatingScale]);

    // ============================================================
    // COMPUTED VALUES - Using useMemo with proper dependencies
    // ============================================================

    // Calculate total from data
    const total = useMemo(() => {
        return data.reduce((sum, item) => sum + item.count, 0);
    }, [data]);

    // Calculate average rating
    const averageRating = useMemo(() => {
        if (total === 0) return 0;
        const weightedSum = data.reduce((sum, item) => sum + (item.rating * item.count), 0);
        return (weightedSum / total).toFixed(2);
    }, [data, total]);

    // Prepare line data - NOW uses getRatingLabel, getRatingColor, getRatingDescription
    // which are defined above
    const actualLineData = useMemo(() => {
        let sourceData = data || [];
        
        if (useAppraisalData && appraisalData) {
            sourceData = appraisalChartData;
        }

        // Use the actual total for percentages
        const effectiveTotal = useAppraisalData ? appraisalTotal : total;

        return sourceData.map(item => ({
            rating: item.rating,
            count: item.count,
            label: getRatingLabel(item.rating),
            percentage: effectiveTotal > 0 ? (item.count / effectiveTotal * 100).toFixed(1) : 0,
            color: getRatingColor(item.rating),
            description: getRatingDescription(item.rating)
        }));
    }, [data, useAppraisalData, appraisalData, appraisalChartData, appraisalTotal, total]);

    // ============================================================
    // HANDLER FUNCTIONS
    // ============================================================

    const handleCompetencyChange = (value) => {
        setSelectedCompetency(value);
        if (onCompetencyChange) {
            onCompetencyChange(value);
        }
    };

    // ============================================================
    // CUSTOM TOOLTIP
    // ============================================================

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const ratingValue = parseFloat(label);
            const ratingLabel = getRatingLabel(ratingValue);
            const ratingColor = getRatingColor(ratingValue);
            const ratingDescription = getRatingDescription(ratingValue);

            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxWidth: '250px'
                }}>
                    <p style={{ margin: 0, fontWeight: 'bold', color: ratingColor }}>
                        {ratingLabel}
                    </p>
                    {ratingDescription && (
                        <p style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#666' }}>
                            {ratingDescription}
                        </p>
                    )}
                    {payload.map((p, index) => (
                        <p key={index} style={{ margin: '5px 0 0 0', color: p.color }}>
                            {p.name}: {p.value} employee(s) ({((p.value / total) * 100).toFixed(1)}%)
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    // ============================================================
    // RENDER
    // ============================================================

    if (total === 0 && !useAppraisalData) {
        return (
            <Card style={{ padding: '20px', textAlign: 'center', height: '100%' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>{title}</h4>
                <p style={{ color: '#999' }}>No {period} review data available</p>
                {competencies.length > 0 && showCompetencyFilter && (
                    <div style={{ marginTop: 16 }}>
                        <select
                            value={selectedCompetency}
                            onChange={(e) => handleCompetencyChange(e.target.value)}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #d0d0d0',
                                fontSize: '14px',
                                width: '100%'
                            }}
                        >
                            <option value="all">All Competencies</option>
                            {competencies.map(comp => (
                                <option key={comp.id} value={comp.id}>{comp.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </Card>
        );
    }

    const selectedCompetencyData = competencies.find(c => c.id === selectedCompetency);
    const effectiveTotal = useAppraisalData ? appraisalTotal : total;
    const effectiveAverage = useAppraisalData ? appraisalAverage : averageRating;

    return (
        <Card style={{ padding: '20px', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#2c3e50' }}>{title}</h4>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                        {period} Review • Total: {effectiveTotal} employees • Avg Rating: {effectiveAverage}
                        {selectedCompetency !== 'all' && (
                            <span style={{
                                marginLeft: '8px',
                                padding: '2px 6px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '4px',
                                display: 'inline-block'
                            }}>
                                Filtered by: {selectedCompetencyData?.name || selectedCompetency}
                            </span>
                        )}
                    </div>
                </div>

                {onViewDetails && (
                    <button
                        onClick={() => onViewDetails(period, selectedCompetency)}
                        style={{
                        backgroundColor: 'rgb(58, 142, 49)',
                        color: '#fff',
                        cursor: 'pointer',
                        borderRadius: 20,
                        border: 'none',
                        height: 36,
                        marginRight: 20,
                        padding: 10,
                      }}
                    >
                        View Details
                    </button>
                )}
            </div>

            <div style={{ width: '100%', height: 400, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={actualLineData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                            dataKey="rating"
                            tickFormatter={getRatingLabel}
                            tick={{ fontSize: 13 }}
                            domain={[0.8, 5.2]}
                            type="number"
                            ticks={[1, 2, 3, 4, 5]}
                            label={{ value: 'Performance Rating', position: 'insideBottom', offset: -15, style: { fontSize: 14 } }}
                        />
                        <YAxis
                            label={{ value: 'Number of Employees', angle: -90, position: 'insideLeft', style: { fontSize: 14 } }}
                            domain={[0, 'auto']}
                            allowDecimals={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            verticalAlign="bottom"       // ← explicitly bottom
                            height={36}                  // ← reserves space for legend row
                            wrapperStyle={{ paddingTop: 26 }}   // ← gap between XAxis label and legend
                        />

                        <Line
                            type="monotone"
                            dataKey="count"
                            name="Actual Distribution by Total Number of All Staff"
                            stroke="#8884d8"
                            strokeWidth={3}
                            dot={(props) => {
                                const { cx, cy, payload } = props;
                                const color = getRatingColor(payload.rating);
                                return (
                                    <circle
                                        cx={cx}
                                        cy={cy}
                                        r={8}
                                        stroke={color}
                                        strokeWidth={3}
                                        fill="white"
                                    />
                                );
                            }}
                            activeDot={{ r: 10 }}
                            connectNulls={true}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Summary Statistics */}
            <div style={{
                marginTop: '20px',
                paddingTop: '12px',
                borderTop: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'space-around',
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                {[1, 2, 3, 4, 5].map(rating => {
                    const count = actualLineData.find(d => d.rating === rating)?.count || 0;
                    const percentage = effectiveTotal > 0 ? (count / effectiveTotal * 100).toFixed(1) : 0;
                    const ratingLabel = getRatingLabel(rating);
                    const ratingColor = getRatingColor(rating);
                    const ratingDescription = getRatingDescription(rating);

                    return (
                        <div key={rating} style={{ textAlign: 'center', flex: 1 }}>
                            <div style={{ fontSize: '0.7rem', color: '#666' }}>
                                {ratingLabel}
                            </div>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: ratingColor }}>
                                {count}
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#999' }}>
                                {percentage}%
                            </div>
                            {ratingDescription && (
                                <div style={{ fontSize: '0.6rem', color: '#666', marginTop: '4px' }}>
                                    {ratingDescription.length > 30
                                        ? ratingDescription.substring(0, 30) + '...'
                                        : ratingDescription}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Summary interpretation */}
            <div style={{
                marginTop: '16px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                fontSize: '0.75rem',
                color: '#666',
                textAlign: 'center'
            }}>
                <strong>Summary:</strong> {effectiveTotal} employee(s) reviewed • Average rating: {effectiveAverage}
                {parseFloat(effectiveAverage) >= 4.5 ? ' (Excellent overall)' :
                 parseFloat(effectiveAverage) >= 3.5 ? ' (Good overall)' :
                 parseFloat(effectiveAverage) >= 2.5 ? ' (Satisfactory overall)' :
                 ' (Needs improvement)'}
            </div>
        </Card>
    );
};

export default BellCurveChart;